import { NetworkStatus, gql, useQuery } from '@apollo/client';
import {
  Avatar,
  Divider,
  IconButton,
  LinkBox,
  LinkOverlay,
  Stack,
  Text,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import * as queryString from 'query-string';
import * as React from 'react';

import { OrganizationRole } from '@/components/OrganizationRole';
import { Search } from '@/components/Search';
import { Sentinel } from '@/components/Sentinel';
import { TableNone } from '@/components/Table';
import { useCurrent } from '@/hooks/useCurrent';
import { usePermissions } from '@/hooks/usePermissions';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { MessageCircleIcon } from '@/imports/ui/chakra/feather';
import { useDebounce } from '@/imports/ui/hooks/useDebounce';

import {
  CompanyRoster_Organization,
  CompanyRoster_OrganizationVariables,
} from './graphql-types/CompanyRoster_Organization';

const ORGANIZATION = gql`
  query CompanyRoster_Organization(
    $singleOrganizationInput: SingleOrganizationInput!
    $rosterInput: RosterInput!
  ) {
    organization(input: $singleOrganizationInput) {
      result {
        _id
        rosterV2(input: $rosterInput) {
          edges {
            node {
              _id
              user {
                _id
                displayName
                jobTitle
                company
                role
                proxyProfilePictureUrl(
                  input: { w: 128, h: 128, fit: "facearea", facepad: 10.0 }
                )
              }
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
`;

const LENGTH = 3;

export const CompanyRoster: React.FC<{ organizationId: string }> = ({
  organizationId,
}) => {
  const current = useCurrent();
  const permissions = usePermissions();

  const [searchText, _setSearchText] = React.useState('');

  const query = useQuery<
    CompanyRoster_Organization,
    CompanyRoster_OrganizationVariables
  >(ORGANIZATION, {
    variables: {
      singleOrganizationInput: { id: organizationId },
      rosterInput: {
        first: LENGTH,
        query: searchText,
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });

  const roster =
    query.data?.organization?.result?.rosterV2 ||
    query.previousData?.organization?.result?.rosterV2;

  const setSearchText = useDebounce(_setSearchText, 500);

  return (
    <Stack spacing="4">
      <Card px="6" py="4">
        <Search
          defaultValue={searchText}
          onChange={setSearchText}
          label="Search people"
          isLoading={query.networkStatus === NetworkStatus.setVariables}
        />
      </Card>

      <Card as={Stack} divider={<Divider />} spacing="4" px="6" py="4">
        {query.networkStatus === NetworkStatus.loading ? (
          <Loading />
        ) : query.error ? (
          <UnexpectedError />
        ) : !roster ? (
          <UnexpectedError />
        ) : roster.edges.length ? (
          roster.edges.map(({ node: { user } }) => (
            <LinkBox key={user._id}>
              <Stack direction="row" alignItems="center" spacing="4">
                <Avatar
                  name={user.displayName}
                  src={user.proxyProfilePictureUrl || undefined}
                />
                <Stack spacing="0" flex="1" alignSelf="stretch">
                  <Stack direction="row" alignItems="center">
                    <LinkOverlay as={NextLink} href={`/members/${user._id}`}>
                      <Text textStyle="bodyStrong" color="gray.700">
                        {user.displayName}
                      </Text>
                    </LinkOverlay>
                    <OrganizationRole role={user.role} />
                  </Stack>
                  {user.jobTitle && (
                    <Text color="gray.600">{user.jobTitle}</Text>
                  )}
                </Stack>
                {current.user?._id !== user._id &&
                  permissions.canAccessCommunityAndAuthenticated && (
                    <NextLink
                      href={`/messages/create?${queryString.stringify({
                        channel_member: user._id,
                      })}`}
                    >
                      <IconButton
                        icon={<MessageCircleIcon color="gray.700" />}
                        variant="outline"
                        colorScheme="gray"
                        aria-label="message user"
                      />
                    </NextLink>
                  )}
              </Stack>
            </LinkBox>
          ))
        ) : (
          <TableNone>No people found</TableNone>
        )}

        {query.networkStatus === NetworkStatus.ready &&
          roster?.pageInfo.hasNextPage && (
            <Sentinel
              onIntersection={() =>
                query.fetchMore({
                  variables: {
                    input: { id: organizationId },
                    rosterInput: {
                      first: LENGTH,
                      after: roster.pageInfo.endCursor,
                      query: searchText,
                    },
                  },
                })
              }
            />
          )}

        {query.networkStatus === NetworkStatus.fetchMore && <Loading />}
      </Card>
    </Stack>
  );
};
